import {
  ApiError,
  call,
  revokeSession,
  signIn as signInOperation,
  WITH_SESSION_COOKIE,
} from '../api';
import { endSession, startSession, type Session } from './sessionStore';

/**
 * Signing in and signing out.
 *
 * `POST /auth/token` returns the token *and* the resolved principal, so a
 * successful sign-in needs exactly one round trip and the browser never decodes
 * a token to find out who it belongs to. Role and company come from the server
 * or they do not come at all.
 *
 * The same response also sets the refresh cookie that lets a reload pick the
 * session back up; nothing here reads or writes it, and nothing here could --
 * it is `httpOnly`. See `restore.ts`.
 */
export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const token = await call(
    signInOperation({
      body: { email, password },
      ...WITH_SESSION_COOKIE,
    }),
  );

  const session: Session = {
    accessToken: token.access_token,
    expiresAt: new Date(token.expires_at),
    principal: token.principal,
  };
  startSession(session);
  return session;
}

/**
 * Leave, here and on the server.
 *
 * The in-memory session goes first and synchronously, so the screen changes the
 * moment the button is pressed rather than after a round trip. The request that
 * follows revokes the refresh token and clears the cookie, which is what makes
 * "sign out, then reload" land on the login screen instead of quietly signing
 * the next person at this machine back in.
 *
 * Its outcome is deliberately not waited on and not surfaced. Signing out has
 * already happened as far as this browser is concerned, and there is nothing
 * useful a person can do about a `/auth/logout` that did not get through --
 * the cookie dies with the browser either way, and the server's copy expires on
 * its own.
 */
export function signOut(): void {
  endSession('signed_out');
  void revokeSession(WITH_SESSION_COOKIE).catch(() => undefined);
}

/**
 * Whether a failed sign-in was a rejected credential rather than a broken
 * system. The API answers every wrong credential with the same 401 and the same
 * message so the endpoint cannot be used to discover which addresses exist --
 * the UI keeps that property by saying the same thing back.
 */
export function isRejectedCredential(error: unknown): boolean {
  return error instanceof ApiError && error.isUnauthenticated;
}
